import { createContext, useState, useEffect, useContext } from "react";
import { auth, db, persistencePromise } from "./firebase";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithCredential,
  sendPasswordResetEmail,
  sendEmailVerification,
} from "firebase/auth";
import { doc, setDoc, getDoc } from "firebase/firestore";
import logger from "./utils/logger";
import { normalizeServerEntitlement } from "./security/proAccess";
import { passwordResetResult } from "./security/authMessages";

const AuthContext = createContext();
const OWNER_LIFETIME_PRO_EMAILS = ["officialknifetastic@gmail.com"];

const EMPTY_ENTITLEMENT = {
  isPro: false,
  proLifetime: false,
  proExpiryDate: null,
  proDaysRemaining: 0,
  proSource: null,
  accountEmail: null,
  paypalEmail: null,
};

const GIS_SCRIPT_SRC = "https://accounts.google.com/gsi/client";
const GIS_LOAD_TIMEOUT_MS = 15000;
const GIS_PROMPT_TIMEOUT_MS = 60000;

let gisScriptPromise = null;
let warnedAboutOrigins = false;

function warnAboutAuthorizedOrigins() {
  if (warnedAboutOrigins) return;
  warnedAboutOrigins = true;
  logger.warn(
    "[Google sign-in] The OAuth 2.0 Web Client ID used for REACT_APP_GOOGLE_CLIENT_ID must list these Authorized JavaScript origins:\n" +
      "  - http://localhost:3000\n" +
      "  - https://aiebookplanner.app\n" +
      "  - https://www.aiebookplanner.app\n" +
      "  - your active Netlify deploy preview domain when testing previews\n" +
      "Configure them at https://console.cloud.google.com/apis/credentials."
  );
}

// OAuth Web Client IDs are public values that ship in the browser bundle, so
// logging the full value is not a secret leak. We mask anyway so logs/screen
// recordings stay tidy and the value isn't easy to misread.
function maskClientId(value) {
  if (typeof value !== "string" || value.length < 24) return "<missing>";
  return `${value.slice(0, 12)}…${value.slice(-8)}`;
}

function isLocalDevOrigin() {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  return host === "localhost" || host === "127.0.0.1" || host === "::1";
}

function missingClientIdMessage() {
  if (isLocalDevOrigin()) {
    return "Google sign-in is not configured for local dev. Add REACT_APP_GOOGLE_CLIENT_ID=<your OAuth 2.0 Web Client ID> to .env.local in the project root, then fully stop and restart `npm start`.";
  }
  return "Google sign-in is not configured. Add REACT_APP_GOOGLE_CLIENT_ID to your Netlify environment variables and redeploy this site (CRA env vars are baked into each build).";
}

function loadGoogleIdentityServices() {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return Promise.reject(
      new Error("Google sign-in is only available in the browser.")
    );
  }

  // We use the OAuth2 token-model API, which lives at
  // `google.accounts.oauth2`. The gsi/client script ships both `id` and
  // `oauth2` namespaces; we check for `oauth2` because that's what this
  // flow actually depends on.
  if (window.google?.accounts?.oauth2) {
    return Promise.resolve(window.google);
  }

  if (gisScriptPromise) {
    return gisScriptPromise;
  }

  gisScriptPromise = new Promise((resolve, reject) => {
    let settled = false;
    const finish = (fn, value) => {
      if (settled) return;
      settled = true;
      fn(value);
    };

    const onLoad = () => {
      if (window.google?.accounts?.oauth2) {
        logger.log("[Google sign-in] GIS script loaded");
        finish(resolve, window.google);
      } else {
        gisScriptPromise = null;
        finish(
          reject,
          new Error("Google Identity Services failed to initialize.")
        );
      }
    };

    const onError = () => {
      gisScriptPromise = null;
      finish(
        reject,
        new Error(
          "Failed to load Google Identity Services. Check your network connection and CSP for accounts.google.com."
        )
      );
    };

    const existing = document.querySelector(`script[src="${GIS_SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", onLoad, { once: true });
      existing.addEventListener("error", onError, { once: true });
    } else {
      const script = document.createElement("script");
      script.src = GIS_SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.addEventListener("load", onLoad, { once: true });
      script.addEventListener("error", onError, { once: true });
      document.head.appendChild(script);
    }

    setTimeout(() => {
      if (!window.google?.accounts?.oauth2) {
        gisScriptPromise = null;
        finish(
          reject,
          new Error("Google Identity Services timed out while loading.")
        );
      }
    }, GIS_LOAD_TIMEOUT_MS);
  });

  return gisScriptPromise;
}

// Map GIS error_callback `type` / token-response `error` strings to user-
// facing messages. Origin mismatches are the most common failure mode in
// local dev, so we surface the actual origin to make the Cloud Console fix
// obvious.
function describeTokenClientError(errorType, fallback) {
  switch (errorType) {
    case "popup_failed_to_open":
      return "Google popup was blocked. Allow popups for this site and try again.";
    case "popup_closed":
    case "access_denied":
    case "user_cancel":
      return "Google sign-in was cancelled.";
    case "origin_mismatch": {
      const origin =
        typeof window !== "undefined" && window.location?.origin
          ? window.location.origin
          : "this site";
      return `Google rejected this site origin. Add ${origin} to Authorized JavaScript origins for this OAuth client in Google Cloud Console, then try again.`;
    }
    case "invalid_client":
      return "Google rejected the OAuth client ID. Verify REACT_APP_GOOGLE_CLIENT_ID matches an existing Web application OAuth 2.0 Client ID.";
    case "invalid_scope":
      return "Google rejected the requested OAuth scopes.";
    case "server_error":
    case "temporarily_unavailable":
      return "Google sign-in is temporarily unavailable. Please try again in a moment.";
    default:
      return fallback || "Google sign-in failed. Please try again.";
  }
}

// OAuth2 token-model flow:
// https://developers.google.com/identity/oauth2/web/guides/use-token-model
// Returns an access_token (NOT an ID token). Pair with
// `GoogleAuthProvider.credential(null, accessToken)` to sign into Firebase.
function requestGoogleAccessToken(google, clientId) {
  return new Promise((resolve, reject) => {
    let resolved = false;
    let timeoutId = null;

    const cleanup = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const settle = (fn, value) => {
      if (resolved) return;
      resolved = true;
      cleanup();
      fn(value);
    };

    let tokenClient;
    try {
      tokenClient = google.accounts.oauth2.initTokenClient({
        client_id: clientId,
        scope: "openid email profile",
        callback: (response) => {
          // Log only safe fields. Never log `access_token`.
          logger.log("[Google sign-in] access-token callback received", {
            hasAccessToken: Boolean(response?.access_token),
            error: response?.error || null,
            error_description: response?.error_description || null,
            error_uri: response?.error_uri || null,
            scope: response?.scope || null,
            expires_in: response?.expires_in || null,
          });

          if (response?.access_token) {
            settle(resolve, response.access_token);
            return;
          }

          if (response?.error) {
            settle(
              reject,
              new Error(
                describeTokenClientError(
                  response.error,
                  `Google sign-in failed: ${response.error_description || response.error}`
                )
              )
            );
            return;
          }

          settle(
            reject,
            new Error("Google sign-in did not return an access token.")
          );
        },
        error_callback: (error) => {
          logger.log("[Google sign-in] token client error_callback", {
            type: error?.type || null,
            message: error?.message || null,
          });
          settle(
            reject,
            new Error(
              describeTokenClientError(
                error?.type,
                error?.message || "Google sign-in failed. Please try again."
              )
            )
          );
        },
      });
      logger.log("[Google sign-in] token client created");
    } catch (error) {
      settle(reject, error);
      return;
    }

    timeoutId = setTimeout(() => {
      settle(
        reject,
        new Error(
          "Google sign-in timed out. Please close any Google popup and try again."
        )
      );
    }, GIS_PROMPT_TIMEOUT_MS);

    try {
      tokenClient.requestAccessToken({ prompt: "select_account" });
    } catch (error) {
      settle(reject, error);
    }
  });
}

export function useAuth() {
  return useContext(AuthContext);
}

function getFirebaseAuthMessage(
  error,
  fallback = "Authentication failed. Please try again."
) {
  switch (error?.code) {
    case "auth/unauthorized-domain":
      return "This domain is not authorized for Firebase sign-in. Add localhost, aiebookplanner.app, and your Netlify domain in Firebase Authentication > Settings > Authorized domains.";
    case "auth/operation-not-allowed":
      return "This sign-in method is not enabled in Firebase Authentication.";
    case "auth/internal-error":
      return "Google sign-in could not finish. Confirm the Firebase authorized domains and Google provider settings, then try again.";
    case "auth/network-request-failed":
      return "Network error during sign-in. Check your connection and try again.";
    case "auth/too-many-requests":
      return "Too many failed attempts. Please try again later.";
    case "auth/invalid-credential":
      return "Google returned an invalid credential. Please try signing in again.";
    default:
      return error?.message || fallback;
  }
}

function normalizeEmail(email) {
  return typeof email === "string" ? email.trim().toLowerCase() : "";
}

// Owner lifetime Pro shows only for a verified owner email, matching the
// server rule; an unverified account with that address gets the server answer.
function isVerifiedOwnerUser(user) {
  return (
    user?.emailVerified === true &&
    OWNER_LIFETIME_PRO_EMAILS.includes(normalizeEmail(user.email))
  );
}

function isGoogleProviderUser(user) {
  return (user?.providerData || []).some(
    (provider) => provider?.providerId === "google.com"
  );
}

async function mergeUserDocument(uid, data) {
  try {
    await setDoc(doc(db, "users", uid), data, { merge: true });
    return true;
  } catch (error) {
    logger.warn("Failed to sync user profile:", {
      code: error?.code,
      message: error?.message,
    });
    return false;
  }
}

async function upsertGoogleUserDocument(user) {
  if (!user) {
    return;
  }

  const userDocRef = doc(db, "users", user.uid);
  const now = new Date().toISOString();

  try {
    const userDoc = await getDoc(userDocRef);

    if (!userDoc.exists()) {
      await setDoc(
        userDocRef,
        {
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL,
          isPro: false,
          createdAt: now,
          lastLogin: now,
          authMethod: "google",
          emailVerified: user.emailVerified,
        },
        { merge: true }
      );
      logger.log("✅ New Google user document created in Firestore");
      return;
    }

    await setDoc(
      userDocRef,
      {
        lastLogin: now,
        emailVerified: user.emailVerified,
        displayName: user.displayName,
        photoURL: user.photoURL,
      },
      { merge: true }
    );
    logger.log("✅ Existing Google user document updated");
  } catch (error) {
    logger.warn("Failed to sync Google user profile:", {
      code: error?.code,
      message: error?.message,
    });
  }
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [entitlement, setEntitlement] = useState(EMPTY_ENTITLEMENT);
  const [authInitialized, setAuthInitialized] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [persistenceReady, setPersistenceReady] = useState(false);

  const {
    isPro,
    proLifetime,
    proExpiryDate,
    proDaysRemaining,
    proSource,
    accountEmail,
    paypalEmail,
  } = entitlement;

  function clearEntitlement() {
    setEntitlement(EMPTY_ENTITLEMENT);
  }

  function applyLifetimeEntitlement(user) {
    setEntitlement({
      isPro: true,
      proLifetime: true,
      proExpiryDate: null,
      proDaysRemaining: null,
      proSource: "owner",
      accountEmail: user?.email || null,
      paypalEmail: null,
    });
  }

  function applySignedInUser(user) {
    if (!user) return;
    setCurrentUser(user);
    setAuthInitialized(true);
    setLoading(false);
    setAuthError(null);
  }

  // onAuthStateChanged is the single source of truth for post-sign-in work.
  // Every sign-in path (email/password, Google) flows through here once the
  // Firebase session settles, so we never run profile sync or Pro checks twice.
  async function syncSignedInUser(user) {
    if (!user) return;

    try {
      await checkProStatus(user);

      if (isGoogleProviderUser(user)) {
        await upsertGoogleUserDocument(user);
        return;
      }

      await mergeUserDocument(user.uid, {
        lastSeen: new Date().toISOString(),
        emailVerified: user.emailVerified,
        lastLogin: new Date().toISOString(),
      });
    } catch (error) {
      logger.error("❌ Post sign-in sync failed:", error);
      setAuthError(
        getFirebaseAuthMessage(
          error,
          "Authentication state could not fully load."
        )
      );
    }
  }

  // Sign up with email/password
  async function signup(email, password) {
    try {
      logger.log("🔄 Creating user account for:", email);
      const result = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      logger.log("✅ User account created successfully, UID:", result.user.uid);

      // Send email verification with enhanced logging
      try {
        logger.log("📧 Attempting to send email verification to:", email);
        logger.log("🔍 User object before verification:", {
          uid: result.user.uid,
          email: result.user.email,
          emailVerified: result.user.emailVerified,
        });

        const verificationResult = await sendEmailVerification(result.user);
        logger.log("✅ Email verification sent successfully to:", email);
        logger.log("📊 Verification result:", verificationResult);

        // Add a small delay to ensure verification email processing completes
        logger.log(
          "⏳ Waiting for verification email processing to complete..."
        );
        await new Promise((resolve) => setTimeout(resolve, 2000)); // 2 second delay

        // Force refresh user to get latest verification status
        try {
          await result.user.reload();
          logger.log(
            "🔄 User reloaded, verification status:",
            result.user.emailVerified
          );
        } catch (reloadError) {
          logger.warn(
            "⚠️ Failed to reload user after verification:",
            reloadError
          );
        }
      } catch (verificationError) {
        logger.error(
          "❌ Failed to send email verification:",
          verificationError
        );
        logger.error("🔍 Verification error details:", {
          code: verificationError.code,
          message: verificationError.message,
          stack: verificationError.stack,
        });

        // Don't throw here - user account was created successfully
        // Just log the error and continue
        logger.warn(
          "⚠️ Continuing with account creation despite verification failure"
        );
      }

      // Create user document in Firestore. This is best-effort so account
      // creation still succeeds if Firestore has a temporary issue.
      await mergeUserDocument(result.user.uid, {
        email: email,
        isPro: false,
        createdAt: new Date().toISOString(),
        authMethod: "email",
        emailVerified: false,
        verificationEmailSent: true,
        verificationAttemptedAt: new Date().toISOString(),
      });
      logger.log("✅ User document sync attempted in Firestore");
      return result;
    } catch (error) {
      logger.error("❌ Sign up error:", error);
      throw error;
    }
  }

  // Sign in with email/password. onAuthStateChanged owns post-sign-in state
  // updates and profile/Pro sync, so we don't race against it here.
  async function login(email, password) {
    try {
      await persistencePromise.catch((error) => {
        logger.warn(
          "Auth persistence was not fully ready before email sign-in:",
          {
            message: error?.message,
          }
        );
      });

      const result = await signInWithEmailAndPassword(auth, email, password);
      logger.log("✅ User logged in successfully with email/password");
      logger.log(
        "📧 Email verification status:",
        result.user.emailVerified ? "Verified" : "Not verified"
      );
      return result;
    } catch (error) {
      logger.error("❌ Login error:", error);
      throw error;
    }
  }

  // Password reset
  async function resetPassword(email) {
    try {
      logger.log("🔄 Sending password reset email to:", email);
      await sendPasswordResetEmail(auth, email);
      logger.log("✅ Password reset email sent to:", email);
      return passwordResetResult(null);
    } catch (error) {
      logger.error("❌ Password reset error:", error);

      // "No such user" counts as sent, so the form never reveals which
      // emails have accounts (see security/authMessages.js).
      const result = passwordResetResult(error.code);
      if (result.success) {
        return result;
      }
      throw new Error(result.message);
    }
  }

  // Google sign-in via Google Identity Services + signInWithCredential. We
  // intentionally avoid Firebase signInWithPopup because its fallback loads
  // the legacy gapi iframe (apis.google.com/js/api.js), which crashed in
  // localhost with "Cannot read properties of undefined (reading 'getContext')".
  async function loginWithGoogle() {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

    // Dev diagnostic so it's obvious whether CRA actually baked the env var
    // into this bundle. CRA reads REACT_APP_* only at `npm start`/`npm run
    // build` time, so a stale dev server is the most common cause of this
    // value being missing even when .env.local is correct.
    logger.log("[Google sign-in] config check", {
      hasClientId: Boolean(clientId),
      clientId: maskClientId(clientId),
      origin: typeof window !== "undefined" ? window.location.origin : null,
      nodeEnv: process.env.NODE_ENV,
    });

    if (!clientId) {
      logger.error(
        "❌ REACT_APP_GOOGLE_CLIENT_ID is not set; Google sign-in is unavailable. " +
          "Origin:",
        typeof window !== "undefined" ? window.location.origin : "(no window)",
        "NODE_ENV:",
        process.env.NODE_ENV
      );
      throw new Error(missingClientIdMessage());
    }

    warnAboutAuthorizedOrigins();

    try {
      if (!persistenceReady) {
        logger.log("⏳ Waiting for auth persistence to be ready...");
        try {
          const persistenceResult = await persistencePromise;
          setPersistenceReady(true);
          if (!persistenceResult) {
            logger.warn(
              "⚠️ Limited persistence available - sign-in may not persist across refreshes"
            );
          }
        } catch (persistenceError) {
          logger.error("❌ Persistence setup failed:", persistenceError);
          setPersistenceReady(true);
          throw new Error(
            "Authentication system failed to initialize. Please refresh the page and try again."
          );
        }
      }

      if (!authInitialized) {
        throw new Error(
          "Authentication system is still initializing. Please wait a moment and try again."
        );
      }

      logger.log("[Google sign-in] Loading Google Identity Services");
      const google = await loadGoogleIdentityServices();

      logger.log(
        "[Google sign-in] Requesting OAuth2 access token via GIS token client"
      );
      const accessToken = await requestGoogleAccessToken(google, clientId);
      logger.log("[Google sign-in] Got access token from GIS", {
        accessTokenLength: accessToken.length,
      });

      // GoogleAuthProvider.credential(idToken, accessToken). The token-model
      // flow returns an access token (not an ID token), so we pass `null` for
      // the first argument and the access token as the second.
      const credential = GoogleAuthProvider.credential(null, accessToken);

      logger.log("[Google sign-in] Calling Firebase signInWithCredential");
      const result = await signInWithCredential(auth, credential);
      logger.log("[Google sign-in] signInWithCredential resolved", {
        uid: result?.user?.uid,
        email: result?.user?.email,
        providerId: result?.providerId || null,
      });

      return result;
    } catch (error) {
      logger.error("❌ Google sign-in error:", error);

      if (error?.code) {
        throw new Error(
          getFirebaseAuthMessage(
            error,
            "Google sign-in failed. Please try again."
          )
        );
      }

      throw error instanceof Error
        ? error
        : new Error("Google sign-in failed. Please try again.");
    }
  }

  // Sign out
  async function logout() {
    try {
      await signOut(auth);
      logger.log("✅ User signed out successfully");
      clearEntitlement();
      setCurrentUser(null);
    } catch (error) {
      logger.error("❌ Sign out error:", error);
      throw error;
    }
  }

  // Check Pro status through the server-authoritative entitlement endpoint.
  // Stores the full normalized entitlement so consumers can read lifetime/expiry
  // metadata without duplicating the fetch.
  async function checkProStatus(userOrId = currentUser) {
    let authUser = null;
    let expectedUid = null;

    // `isCurrent()` answers: is the user we started this check for still the
    // signed-in user right now? After every await we use it to make sure a
    // stale request can't write the previous account's entitlement onto a
    // sign-out or account-switch that happened mid-flight.
    const isCurrent = () => auth.currentUser?.uid === expectedUid;

    try {
      authUser =
        typeof userOrId === "string"
          ? auth.currentUser?.uid === userOrId
            ? auth.currentUser
            : null
          : userOrId;

      if (!authUser) {
        if (!userOrId || !auth.currentUser) {
          clearEntitlement();
        }
        return false;
      }

      expectedUid = authUser.uid;

      if (isVerifiedOwnerUser(authUser)) {
        if (!isCurrent()) {
          return false;
        }
        applyLifetimeEntitlement(authUser);
        return true;
      }

      const idToken = await authUser.getIdToken();
      if (!isCurrent()) {
        return false;
      }

      const response = await fetch("/.netlify/functions/get-user-status", {
        method: "GET",
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });
      if (!isCurrent()) {
        return false;
      }

      if (!response.ok) {
        if (isCurrent()) {
          clearEntitlement();
        }
        return false;
      }

      const status = await response.json();
      if (!isCurrent()) {
        return false;
      }

      const normalized = normalizeServerEntitlement(status);

      setEntitlement({
        isPro: normalized.isPro,
        proLifetime: normalized.lifetime,
        proExpiryDate: normalized.expiryDate,
        proDaysRemaining: normalized.daysRemaining,
        proSource: status.source || null,
        accountEmail:
          status.accountEmail || status.paypalEmail || authUser.email || null,
        paypalEmail: status.paypalEmail || null,
      });

      return normalized.isPro;
    } catch (error) {
      logger.error("Error checking Pro status:", error);
      if (expectedUid && !isCurrent()) {
        // The user changed while we were awaiting; leave entitlement state
        // alone for the new session.
        return false;
      }
      if (isVerifiedOwnerUser(authUser)) {
        applyLifetimeEntitlement(authUser);
        return true;
      }
      clearEntitlement();
      return false;
    }
  }

  // Activate pro with PayPal verification
  async function activateProWithPayPal(email, transactionId) {
    if (!currentUser) {
      return { success: false, message: "Please sign in first" };
    }

    try {
      const idToken = await currentUser.getIdToken();
      const response = await fetch("/.netlify/functions/verify-payment", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ email, transactionId }),
      });

      const data = await response.json();

      if (data.valid) {
        // Re-fetch the canonical server entitlement so the context's lifetime
        // and expiry metadata match what the server just recorded.
        await checkProStatus(currentUser);
        logger.log("Pro access activated successfully");
        return {
          success: true,
          daysRemaining: data.daysRemaining,
          expiryDate: data.expiryDate,
          lifetime: data.lifetime === true,
        };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      logger.error("❌ Pro activation error:", error);
      return {
        success: false,
        message: "Unable to verify payment. Please try again.",
      };
    }
  }

  // Refresh user's email verification status
  async function refreshUser() {
    if (!currentUser) {
      throw new Error("No user is currently signed in.");
    }

    try {
      logger.log("🔄 Refreshing user data...");
      await currentUser.reload();
      logger.log("✅ User data refreshed");
      logger.log(
        "📧 Email verification status:",
        currentUser.emailVerified ? "Verified" : "Not verified"
      );

      // Update Firestore with latest verification status. Refresh succeeds even
      // if this profile sync is temporarily unavailable.
      await mergeUserDocument(currentUser.uid, {
        emailVerified: currentUser.emailVerified,
        lastSeen: new Date().toISOString(),
      });

      return {
        success: true,
        emailVerified: currentUser.emailVerified,
        message: "User data refreshed successfully",
      };
    } catch (error) {
      logger.error("❌ Failed to refresh user data:", error);
      throw new Error(
        `Failed to refresh user data: ${error.message || "Unknown error"}`
      );
    }
  }

  // Get verification blocking state
  function getVerificationBlockingState() {
    if (!currentUser) {
      return { blocked: false, reason: null };
    }

    if (!currentUser.emailVerified && !isPro) {
      return {
        blocked: true,
        reason: "email_verification_required",
        message: "Please verify your email address to access the application.",
      };
    }

    return { blocked: false, reason: null };
  }

  // Check if user has full access. A server-verified Pro entitlement also
  // unlocks access, which covers paid accounts and owner lifetime accounts.
  function hasFullAccess() {
    return Boolean(currentUser && (currentUser.emailVerified || isPro));
  }

  // Check if user email is verified
  function isEmailVerified() {
    return currentUser ? currentUser.emailVerified : false;
  }

  // Resend email verification
  async function resendEmailVerification() {
    if (!currentUser) {
      throw new Error("No user is currently signed in.");
    }

    if (currentUser.emailVerified) {
      throw new Error("Email is already verified.");
    }

    try {
      logger.log("🔄 Resending email verification to:", currentUser.email);
      logger.log("🔍 Current user state:", {
        uid: currentUser.uid,
        email: currentUser.email,
        emailVerified: currentUser.emailVerified,
      });

      const verificationResult = await sendEmailVerification(currentUser);
      logger.log("✅ Email verification resent successfully");
      logger.log("📊 Resend verification result:", verificationResult);

      // Add a small delay to ensure verification email processing completes
      logger.log("⏳ Waiting for verification email processing to complete...");
      await new Promise((resolve) => setTimeout(resolve, 2000)); // 2 second delay

      let verificationResendCount = 0;
      try {
        const userDoc = await getDoc(doc(db, "users", currentUser.uid));
        verificationResendCount = userDoc.data()?.verificationResendCount || 0;
      } catch (error) {
        logger.warn("Failed to read verification resend count:", {
          code: error?.code,
          message: error?.message,
        });
      }

      await mergeUserDocument(currentUser.uid, {
        verificationEmailSent: true,
        verificationResentAt: new Date().toISOString(),
        verificationResendCount: verificationResendCount + 1,
      });

      return {
        success: true,
        message: "Verification email sent! Check your inbox.",
      };
    } catch (error) {
      logger.error("❌ Failed to resend email verification:", error);
      logger.error("🔍 Resend verification error details:", {
        code: error.code,
        message: error.message,
        stack: error.stack,
      });

      if (error.code === "auth/too-many-requests") {
        throw new Error(
          "Too many verification email attempts. Please try again later."
        );
      } else if (error.code === "auth/network-request-failed") {
        throw new Error(
          "Network error. Please check your internet connection and try again."
        );
      } else {
        throw new Error(
          `Failed to resend verification email: ${error.message || "Unknown error"}`
        );
      }
    }
  }

  // Set up auth state listener - FIXED to wait for persistence
  useEffect(() => {
    let unsubscribe = null;
    // `cancelled` is flipped in the cleanup callback. Because initializeAuth
    // awaits persistence before subscribing, the provider can unmount during
    // that await; without this flag the awaited code would resume after
    // unmount and register an onAuthStateChanged listener that never gets
    // torn down, leaking state updates on a dead component.
    let cancelled = false;

    async function initializeAuth() {
      try {
        // Wait for persistence to be set up BEFORE setting up auth listener
        logger.log("🔄 Waiting for auth persistence to be ready...");

        // Add timeout to prevent hanging
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(
            () => reject(new Error("Auth initialization timeout")),
            15000
          ); // 15 second timeout
        });

        const persistenceResult = await Promise.race([
          persistencePromise,
          timeoutPromise,
        ]);
        if (cancelled) {
          return;
        }
        setPersistenceReady(true);

        if (persistenceResult) {
          logger.log(
            "✅ Auth persistence ready, setting up auth state listener..."
          );
        } else {
          logger.warn(
            "⚠️ Auth persistence limited - sessions may not persist across refreshes"
          );
        }

        if (cancelled) {
          return;
        }

        // Now set up the auth state listener
        unsubscribe = onAuthStateChanged(auth, async (user) => {
          if (cancelled) {
            return;
          }
          logger.log(
            "🔄 Auth state changed:",
            user ? `User: ${user.email} (UID: ${user.uid})` : "No user"
          );
          logger.log(
            "📊 Persistence status:",
            persistenceResult ? "Full" : "Limited"
          );

          if (user) {
            applySignedInUser(user);
          } else {
            setCurrentUser(null);
            clearEntitlement();
            setAuthInitialized(true);
            setLoading(false);
          }

          if (!user) {
            logger.log("ℹ️ No user session found");
            return;
          }

          logger.log("✅ User authenticated, checking Pro status...");
          logger.log(
            "📧 Email verification status:",
            user.emailVerified ? "Verified" : "Not verified"
          );
          syncSignedInUser(user);
        });
      } catch (error) {
        if (cancelled) {
          return;
        }
        logger.error("❌ Failed to initialize auth:", error);
        setAuthError("Failed to initialize authentication");
        setLoading(false);
        setAuthInitialized(true);
        setPersistenceReady(true); // Set to true even on error to prevent infinite loading

        if (cancelled) {
          return;
        }

        // Set up basic auth listener even if persistence fails
        try {
          unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (cancelled) {
              return;
            }
            if (user) {
              applySignedInUser(user);
            } else {
              setCurrentUser(null);
              clearEntitlement();
              setLoading(false);
              setAuthInitialized(true);
            }

            if (!user) {
              return;
            }

            syncSignedInUser(user);
          });
        } catch (fallbackError) {
          logger.error("❌ Even fallback auth listener failed:", fallbackError);
        }
      }
    }

    // Initialize auth
    initializeAuth();

    // Cleanup function
    return () => {
      cancelled = true;
      if (unsubscribe && typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, []);

  const value = {
    currentUser,
    isPro,
    proLifetime,
    proExpiryDate,
    proDaysRemaining,
    proSource,
    accountEmail,
    paypalEmail,
    loading,
    authInitialized,
    authError,
    persistenceReady,
    signup,
    login,
    loginWithGoogle,
    logout,
    resetPassword,
    activateProWithPayPal,
    checkProStatus,
    resendEmailVerification,
    isEmailVerified,
    refreshUser,
    hasFullAccess,
    getVerificationBlockingState,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
